import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"
import type { DotProps } from "@/v2-backoffice-client/components/dot"
import {
  POLICY_FIELD_KEYS,
  PRIVACY_CUSTOM_POLICY_LABEL,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.constants"
import type {
  PolicyFieldCodes,
  PolicyFieldKey,
  PrivacyField,
  PrivacyFieldChange,
  PrivacyPolicy,
  PrivacyPreset,
  PrivacySummary,
  PrivacyVisibilityType,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export function isPolicyFieldKey(
  columnName: string,
): columnName is PolicyFieldKey {
  return (POLICY_FIELD_KEYS as readonly string[]).includes(columnName)
}

/** A policy row reduced to its eight codes — the shape the upsert sends. */
export function policyFieldCodes(policy: PrivacyPolicy): PolicyFieldCodes {
  const codes = {} as PolicyFieldCodes
  for (const key of POLICY_FIELD_KEYS) codes[key] = policy[key]
  return codes
}

/**
 * The edited cells that still differ from the server row — a row's
 * dirtiness, decided at render time so it always compares against what the
 * server has NOW. Null when nothing differs.
 */
export function dirtyFieldCodes(
  policy: PrivacyPolicy,
  edits: Partial<PolicyFieldCodes> | undefined,
): Partial<PolicyFieldCodes> | null {
  if (!edits) return null
  const changed: Partial<PolicyFieldCodes> = {}
  let hasChanges = false
  for (const key of POLICY_FIELD_KEYS) {
    const code = edits[key]
    if (code === undefined || code === policy[key]) continue
    changed[key] = code
    hasChanges = true
  }
  return hasChanges ? changed : null
}

/**
 * Colder-to-hotter with how much personal data the code exposes: hidden is
 * neutral, licensed-users-only is amber, always-visible is the loudest.
 * Codes are data (acc.PrivateDataVisibilityTypes), so an unseeded newcomer
 * renders too, just without an opinion.
 */
export function visibilityDotVariant(code: number): DotProps["variant"] {
  if (code === 1) return "muted"
  if (code === 2) return "warning"
  if (code === 3) return "orange"
  return "secondary"
}

/**
 * Tone of the "current policy" pill: on a recommended preset green, on any
 * other preset amber (the seed marks All open as not recommended for a
 * reason), off every preset — custom rows — grey.
 */
export function presetBadgeVariant(
  preset: Pick<PrivacyPreset, "IsRecommended"> | null | undefined,
): BadgeVariant {
  if (!preset) return "neutral"
  return preset.IsRecommended ? "positive" : "warning"
}

/**
 * A preset's display name. Name is read from the Language JSON column, so a
 * blob without the requested locale yields null; fall back to the code
 * rather than an empty heading or an empty dialog title.
 */
export function presetLabel(preset: Pick<PrivacyPreset, "Code" | "Name">) {
  return preset.Name ?? `Preset ${preset.Code}`
}

/**
 * What a team's policy is called at a glance: the matched preset's name, or
 * Custom. Null when the summary itself is missing (the core-db read failed
 * for that row), so a table cell or export can stay empty rather than lie.
 * The one place this wording lives — the pill, the list's grouping value and
 * the export all read it.
 */
export function privacyPolicyLabel(
  summary: PrivacySummary | null | undefined,
): string | null {
  if (!summary) return null
  return summary.MatchesPreset
    ? presetLabel(summary.MatchesPreset)
    : PRIVACY_CUSTOM_POLICY_LABEL
}

/**
 * A visibility code's display name from the catalogue, falling back to the
 * code itself — the history can carry a code the catalogue no longer names.
 */
export function visibilityTypeLabel(
  code: number,
  visibilityTypes: PrivacyVisibilityType[],
) {
  const type = visibilityTypes.find((candidate) => candidate.Code === code)
  return type?.Name ?? `Code ${code}`
}

/** The field changes of one source that share the same before → after. */
export type FieldChangeGroup = {
  fromCode: number
  toCode: number
  columnNames: string[]
}

/**
 * Collapses a source's field changes into one entry per transition, in the
 * order the fields first appear (the catalogue's). A preset moves most of a
 * row's eight fields the same way, so "eight lines of Hidden → Always
 * visible" becomes one — which is what makes a history entry readable.
 */
export function groupFieldChanges(
  changes: PrivacyFieldChange[],
): FieldChangeGroup[] {
  const groups: FieldChangeGroup[] = []
  for (const change of changes) {
    const group = groups.find(
      (candidate) =>
        candidate.fromCode === change.FromCode &&
        candidate.toCode === change.ToCode,
    )
    if (group) group.columnNames.push(change.Field)
    else {
      groups.push({
        fromCode: change.FromCode,
        toCode: change.ToCode,
        columnNames: [change.Field],
      })
    }
  }
  return groups
}

/** A field as the history names it: display name plus whether it counts. */
export type NamedField = {
  columnName: string
  label: string
  /** False for the four fields the person procedures never disclose. */
  isEnforced: boolean
}

/**
 * How to say which fields a transition covers without listing eight names:
 * all of them, all but one or two, or the plain list.
 */
export type FieldGroupDescription =
  | { kind: "every" }
  | { kind: "except"; fields: NamedField[] }
  | { kind: "list"; fields: NamedField[] }

/**
 * Names a set of fields against the catalogue, in catalogue order. A column
 * the catalogue does not know (a ninth field arriving before this client
 * does) keeps its ColumnName and counts as enforced, so it is never hidden.
 */
export function describeFieldGroup(
  columnNames: string[],
  catalog: PrivacyField[],
): FieldGroupDescription {
  const known = catalog.map<NamedField>((field) => ({
    columnName: field.ColumnName,
    label: field.Name ?? field.ColumnName,
    isEnforced: field.IsEnforced,
  }))
  const unknown = columnNames
    .filter((name) => !known.some((field) => field.columnName === name))
    .map<NamedField>((name) => ({
      columnName: name,
      label: name,
      isEnforced: true,
    }))
  const all = [...known, ...unknown]

  const included = all.filter((field) => columnNames.includes(field.columnName))
  const missing = all.filter((field) => !columnNames.includes(field.columnName))

  if (missing.length === 0) return { kind: "every" }
  // "Every field except Name" beats seven names in a row; below four fields
  // in total the exception form stops being shorter than the list.
  if (all.length >= 4 && missing.length <= 2) {
    return { kind: "except", fields: missing }
  }
  return { kind: "list", fields: included }
}
